import { z } from 'zod';

/**
 * F35 — Trainer Billing Model frozen contracts.
 *
 * Every trainer carries exactly one ACTIVE billing plan at any time:
 *   - `commission` — the platform bills 15% (1500 bp) of every PAID trainer
 *     session, snapshotted per accrual (admin may override the rate per trainer).
 *   - `flat_monthly` — a fixed $20.00/mo subscription fee, billed on each period
 *     close regardless of session count.
 *
 * Plan switches are QUEUED to the next billing-period boundary (D4). Admin
 * overrides (plan / rate / trial) apply immediately (D10). Trainer invoices ride
 * F23's `commission_statements` state machine (`draft → issued → settled → void`)
 * with `source ∈ {trainer_commission, trainer_flat}` — see `commission.ts`.
 *
 * All monetary fields are USD minor units (integer, cents).
 *
 * See plans/35_F35_trainer_billing_model.md §4. Wave-2 sub-agents develop
 * against these contracts in parallel — DO NOT rename/drop/add fields without
 * escalating to the orchestrator first (contract-amendment protocol).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const TRAINER_BILLING_MODES = ['commission', 'flat_monthly'] as const;
export type TrainerBillingMode = (typeof TRAINER_BILLING_MODES)[number];

/** D1 — platform default trainer commission rate (15.00%). */
export const TRAINER_DEFAULT_COMMISSION_BASIS_POINTS = 1500 as const;
/** D2 — single flat-monthly tier ($20.00 USD minor units). */
export const TRAINER_FLAT_MONTHLY_FEE_AMOUNT = 2000 as const;
export const TRAINER_FLAT_MONTHLY_FEE_CURRENCY = 'USD' as const;
/** Grace days from issue to due (drives `trainer.invoice_overdue`). */
export const TRAINER_INVOICE_DUE_GRACE_DAYS = 14 as const;

/** Admin rate-override bounds (0%–50%); mirrors F23 venue rate bounds. */
export const TRAINER_MIN_RATE_BASIS_POINTS = 0 as const;
export const TRAINER_MAX_RATE_BASIS_POINTS = 5000 as const;

/** Who set a plan-assignment row. */
export const TRAINER_PLAN_ACTOR_KINDS = ['trainer', 'admin', 'system'] as const;
export type TrainerPlanActorKind = (typeof TRAINER_PLAN_ACTOR_KINDS)[number];

// ─── Read-side projections ───────────────────────────────────────────────────

/**
 * One row of the append-only `trainer_billing_plans` history. The ACTIVE plan
 * for a trainer is the most recent row whose `effectiveAt` is ≤ now(); a QUEUED
 * switch is the single row whose `effectiveAt` is > now().
 */
export type TrainerBillingPlan = {
  readonly trainerUserId: string;
  readonly mode: TrainerBillingMode;
  /** Present iff `mode='flat_monthly'` (snapshot of the tier fee). */
  readonly flatFeeAmount: number | null;
  readonly flatFeeCurrency: string;
  /** ISO 8601. ≤ now() for the active plan; > now() for a queued switch. */
  readonly effectiveAt: string;
  /** Admin-granted trial end (D6); while now() < this, billing is skipped. */
  readonly trialEndsAt: string | null;
  readonly setByActorKind: TrainerPlanActorKind;
  readonly reason: string | null;
};

/**
 * The trainer's billing snapshot returned by `GET /me/trainer/billing` +
 * `GET /admin/trainers/:id/billing`. Carries the active plan, any queued
 * switch (D4 banner), and the resolved effective commission rate.
 */
export type TrainerActiveBilling = {
  readonly active: TrainerBillingPlan;
  /** The future-dated assignment row, if a switch is queued. */
  readonly queuedSwitch: TrainerBillingPlan | null;
  /** Resolved commission rate (override ?? 1500). Meaningful when mode='commission'. */
  readonly effectiveRateBasisPoints: number;
  readonly rateSource: 'default' | 'admin_override';
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * `POST /me/trainer/billing/switch` body — trainer self-switches to the other
 * mode. The switch is QUEUED to the next period boundary (D4). Service rejects
 * switching to the mode you're already on / already queued for
 * (`TRAINER_PLAN_SWITCH_NO_CHANGE` / `TRAINER_PLAN_SWITCH_ALREADY_QUEUED`).
 */
export const SwitchTrainerPlanInputSchema = z
  .object({
    mode: z.enum(TRAINER_BILLING_MODES),
  })
  .strict();
export type SwitchTrainerPlanInput = z.infer<typeof SwitchTrainerPlanInputSchema>;

/**
 * `PUT /admin/trainers/:trainerUserId/billing/override` body — admin override
 * (D10). Applies IMMEDIATELY (not queued). At least one of mode / rate / trial
 * must be present. `reason` (10–500) is mandatory + audit-logged + forward-only
 * (never rewrites issued invoices, D11).
 */
export const AdminTrainerBillingOverrideInputSchema = z
  .object({
    mode: z.enum(TRAINER_BILLING_MODES).optional(),
    rateBasisPoints: z
      .number()
      .int()
      .min(TRAINER_MIN_RATE_BASIS_POINTS)
      .max(TRAINER_MAX_RATE_BASIS_POINTS)
      .optional(),
    trialEndsAt: z.string().datetime().nullable().optional(),
    reason: z.string().trim().min(10).max(500),
  })
  .strict()
  .refine(
    (v) => v.mode !== undefined || v.rateBasisPoints !== undefined || v.trialEndsAt !== undefined,
    { message: 'At least one of mode, rateBasisPoints, or trialEndsAt must be provided' },
  );
export type AdminTrainerBillingOverrideInput = z.infer<
  typeof AdminTrainerBillingOverrideInputSchema
>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F35 trainer-billing error vocabulary. Service maps to HTTP:
 *   - `*_NOT_FOUND` → 404
 *   - `*_NO_CHANGE` / `*_ALREADY_QUEUED` / `*_NOTHING_TO_CANCEL` / `*_OUT_OF_BOUNDS`
 *     / `*_OVERRIDE_EMPTY` → 422
 */
export const TRAINER_BILLING_ERROR_CODES = [
  'TRAINER_BILLING_PLAN_NOT_FOUND',
  'TRAINER_PLAN_SWITCH_NO_CHANGE',
  'TRAINER_PLAN_SWITCH_ALREADY_QUEUED',
  'TRAINER_PLAN_SWITCH_NOTHING_TO_CANCEL',
  'TRAINER_RATE_OVERRIDE_OUT_OF_BOUNDS',
  'TRAINER_RATE_OVERRIDE_NO_CHANGE',
  'TRAINER_BILLING_OVERRIDE_EMPTY',
] as const;
export type TrainerBillingErrorCode = (typeof TRAINER_BILLING_ERROR_CODES)[number];
